import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { fetchAllUserUnits } from '../../services/entityServices'
import { getUserDataByEmail } from '../../services/userServices'
import i18n from '../../i18n'
import { setAlertsFilters } from './alertSlice'
import { resetOrgScopedData } from './orgSlice'

export interface IUserQueryParams {
    orgId: string
    email: string
}
export interface UserPreferences {
    units: { [string: string]: string }
    orgPermissionDetails: any[]
    userName: string
    localization?: LocalizationObject
    alertFilterPreference: any
}
export interface UserData {
    auth0Id: string
    createdAt: string
    email: string
    id: string
    modifiedAt: string
    name: string
    role: string
    status: string
    picture: string
    preferences: UserPreferences
}
export interface UnitsList {
    id: string
    units: any[]
    defaultUnit: any
    selected?: any
}

// localization interface
export interface LocalizationObject {
    dateFormat: string
    timeFormat: string
    timeZone?: string | null
    language: string
}

interface UserType {
    user: UserData | null
    unitsList: UnitsList[]
}

const initialState: UserType = {
    user: null,
    unitsList: [],
}

export const getUserData = createAsyncThunk(
    'user/getUserData',
    async (userQueryParams: IUserQueryParams, { dispatch }) => {
        const response = await getUserDataByEmail(
            userQueryParams.orgId,
            userQueryParams.email,
        )
        const localization = response?.preferences?.localization
        await i18n.changeLanguage(localization?.language || 'en')

        const alertFilterPreference =
            response?.preferences?.alertFilterPreference
        if (alertFilterPreference) {
            dispatch(setAlertsFilters(alertFilterPreference))
        }

        return response || null
    },
)

export const getUserUnitsList = createAsyncThunk(
    'user/getUserUnitsList',
    async () => {
        const res = await fetchAllUserUnits()
        return res
    },
)

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserAlertPreferences: (state, action) => {
            if (state.user) {
                state.user.preferences.alertFilterPreference = action.payload
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(resetOrgScopedData, (state) => {
                state.user = null
            })
            .addCase(getUserData.fulfilled, (state, action) => {
                state.user = action.payload
            })
            .addCase(getUserData.rejected, (state) => {
                state.user = null
            })
            .addCase(getUserUnitsList.fulfilled, (state, action) => {
                state.unitsList = action.payload
            })
            .addCase(getUserUnitsList.rejected, (state) => {
                state.unitsList = []
            })
    },
})

export const { setUserAlertPreferences } = userSlice.actions
export default userSlice.reducer
